import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { inputLocalParaUtc, utcParaInputLocal } from "@shared/timezone";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ptBR } from "date-fns/locale";
import MeetingSheet from "@/components/Agenda/MeetingSheet";
import AvailabilityPanel from "@/components/Agenda/AvailabilityPanel";
import WeekGrid, { type Bloqueio, type Densidade } from "@/components/Agenda/WeekGrid";
import MonthGrid from "@/components/Agenda/MonthGrid";
import BlockDialog, { type BlockDefaults, type BloqueioEditavel } from "@/components/Agenda/BlockDialog";
import {
  STATUS_REUNIAO,
  TIPO_LOCAL,
  diasDaSemana,
  diasDoMes,
  iniciais,
  primeiroNome,
  tomDoAvatar,
  duracaoEmMinutos,
  horarioComFuso,
  rotuloDeDuracao,
  somaDias,
} from "@/components/Agenda/agenda-utils";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  Briefcase,
  CalendarOff,
  CalendarDays,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Clock,
  Columns3,
  LayoutGrid,
  Settings,
  RectangleVertical,
  ZoomIn,
  ZoomOut,
  ExternalLink,
  MapPin,
  Plus,
  Users,
  Video,
} from "lucide-react";

type Visao = "mes" | "semana" | "dia";
type Origem = "tudo" | "reunioes" | "bloqueios" | "google";

const DENSIDADES_ORDEM: Densidade[] = ["compacto", "normal", "amplo"];
const CHAVE_DENSIDADE = "agenda.densidade";

/** Densidade lembrada por navegador — é preferência de tela, não de conta. */
function lerDensidade(): Densidade {
  try {
    const v = localStorage.getItem(CHAVE_DENSIDADE);
    if (v && (DENSIDADES_ORDEM as string[]).includes(v)) return v as Densidade;
  } catch {
    /* sem storage (modo privado etc.): fica no padrão */
  }
  return "normal";
}

function primeiraMaiuscula(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function AgendaContent() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const meuId = (user as any)?.id as number | undefined;

  const podeVerTodos = can("agenda:view_all");
  const podeMarcar = can("agenda:manage") || can("agenda:manage_all");

  const [ancora, setAncora] = useState(() => new Date());
  // `?tab=disponibilidade` é para onde a volta do OAuth do Google aponta.
  const [configAberta, setConfigAberta] = useState(false);
  const [aba, setAba] = useState<string>(() =>
    new URLSearchParams(window.location.search).get("tab") === "disponibilidade" ? "disponibilidade" : "calendario",
  );
  const [visao, setVisao] = useState<Visao>("semana");
  const [densidade, setDensidade] = useState<Densidade>(lerDensidade);
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [pessoa, setPessoa] = useState<string>("all");
  // O que mostrar na grade: tudo, só reuniões, só bloqueios ou só o que veio do Google.
  const [origem, setOrigem] = useState<Origem>("tudo");
  const [sheetAberto, setSheetAberto] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [defaults, setDefaults] = useState<{ startAt?: Date } | undefined>(undefined);
  const [detalheId, setDetalheId] = useState<number | null>(null);
  const [bloqueioDialogo, setBloqueioDialogo] = useState<{
    aberto: boolean;
    bloqueio: BloqueioEditavel | null;
    defaults?: BlockDefaults;
  }>({ aberto: false, bloqueio: null });

  // Recado da volta do OAuth do Google (mesmo desenho do Canva em Settings):
  // o servidor manda um CÓDIGO, e a frase mora aqui.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get("google");
    if (!google) return;
    const recados: Record<string, { tipo: "ok" | "erro"; texto: string }> = {
      autorizado: { tipo: "ok", texto: "Google Agenda conectado. Trazendo seus compromissos…" },
      cancelado: { tipo: "erro", texto: "Você não concluiu a autorização no Google." },
      sem_permissao: { tipo: "erro", texto: "Seu perfil não tem acesso à agenda." },
      nao_configurado: { tipo: "erro", texto: "A conexão com o Google ainda não está configurada neste ambiente." },
      sessao_divergente: { tipo: "erro", texto: "A conexão foi iniciada em outra sessão. Comece de novo por aqui." },
      state_invalido: { tipo: "erro", texto: "A autorização demorou demais e expirou. Tente novamente." },
      sem_codigo: { tipo: "erro", texto: "O Google não devolveu a autorização." },
      sem_escopo: { tipo: "erro", texto: "Na tela do Google, marque a permissão do Google Agenda — sem ela não dá para ler nem criar compromissos. Tente conectar de novo." },
      sem_refresh: { tipo: "erro", texto: "O Google não liberou acesso contínuo. Remova o LeadConversa em myaccount.google.com/permissions e conecte de novo." },
      falha_troca: { tipo: "erro", texto: "Não consegui concluir a conexão com o Google. Tente de novo." },
      falha_inicio: { tipo: "erro", texto: "Não consegui iniciar a conexão com o Google. Tente de novo." },
    };
    const recado = recados[google];
    if (recado?.tipo === "ok") toast.success(recado.texto);
    else if (recado) toast.error(recado.texto);
    params.delete("google");
    params.delete("tab");
    const resto = params.toString();
    window.history.replaceState({}, "", resto ? `?${resto}` : window.location.pathname);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_DENSIDADE, densidade);
    } catch {
      /* idem */
    }
  }, [densidade]);

  // Semana: os 7 dias a partir do domingo. Dia: só a âncora, à meia-noite.
  // Mês: semanas inteiras (dom–sáb) que cobrem o mês, como o Google.
  const dias = useMemo(() => {
    if (visao === "semana") return diasDaSemana(ancora);
    if (visao === "mes") return diasDoMes(ancora);
    const d = new Date(ancora);
    d.setHours(0, 0, 0, 0);
    return [d];
  }, [ancora, visao]);
  const inicio = dias[0];
  const fim = somaDias(dias[dias.length - 1], 1);
  const passo = visao === "semana" ? 7 : 1;

  const availabilityQuery = trpc.agenda.availability.list.useQuery({ userId: pessoa !== "all" ? Number(pessoa) : undefined });
  const faixas = availabilityQuery.data ?? [];
  // Sem expediente no fim de semana (de quem está na tela), sábado e domingo
  // saem da grade — como o "ocultar fins de semana" do Google. Sem semana
  // típica cadastrada, mostra os 7: não dá para saber.
  const semFimDeSemana = faixas.length > 0 && !faixas.some((f: any) => f.dayOfWeek === 0 || f.dayOfWeek === 6);
  const [mostrarFimDeSemana, setMostrarFimDeSemana] = useState(false);
  const esconderFimDeSemana = semFimDeSemana && !mostrarFimDeSemana && visao !== "dia";
  const diasVisiveis = useMemo(
    () => (esconderFimDeSemana ? dias.filter((d) => d.getDay() !== 0 && d.getDay() !== 6) : dias),
    [dias, esconderFimDeSemana],
  );

  function navegar(direcao: 1 | -1) {
    setAncora((d) => {
      if (visao !== "mes") return somaDias(d, direcao * passo);
      // Dia 1 evita pular mês (31/01 + 1 mês cairia em março).
      return new Date(d.getFullYear(), d.getMonth() + direcao, 1);
    });
  }

  function mudarDensidade(direcao: 1 | -1) {
    setDensidade((d) => {
      const i = DENSIDADES_ORDEM.indexOf(d) + direcao;
      return DENSIDADES_ORDEM[Math.min(DENSIDADES_ORDEM.length - 1, Math.max(0, i))];
    });
  }

  const usersQuery = trpc.agenda.meetings.listUsers.useQuery(undefined, {
    enabled: podeVerTodos,
  });

  const meetingsQuery = trpc.agenda.meetings.list.useQuery({
    from: inicio,
    to: fim,
    userId: pessoa !== "all" ? Number(pessoa) : undefined,
  });

  // Bloqueios e semana típica entram na grade junto das reuniões: é o que
  // responde "essa pessoa está livre aí?" sem precisar abrir a outra aba.
  // Mesmo recorte de pessoa que as reuniões — sem VIEW_ALL o servidor já
  // reduz ao próprio usuário.
  // A agenda que está na tela: período visível e reuniões nele. É o que
  // permite "tem horário livre nessa semana?" sem repetir as datas.
  useContextoDeLista({
    titulo: "Agenda",
    filtros: { de: inicio, ate: fim, pessoa: pessoa === "all" ? "todos" : pessoa },
    itens: (meetingsQuery.data ?? []).map((m: any) => ({
      id: m.meeting?.id ?? m.id,
      titulo: m.meeting?.title ?? m.title,
      inicio: m.meeting?.startAt ?? m.startAt,
      fim: m.meeting?.endAt ?? m.endAt,
      status: m.meeting?.status ?? m.status,
    })),
  });

  const filtroPessoa = pessoa !== "all" ? Number(pessoa) : undefined;
  const blocksQuery = trpc.agenda.blocks.list.useQuery({
    from: inicio,
    to: fim,
    userId: filtroPessoa,
  });

  const reunioes = useMemo(() => {
    const todas = meetingsQuery.data ?? [];
    return origem === "tudo" || origem === "reunioes" ? todas : [];
  }, [meetingsQuery.data, origem]);
  const bloqueios = useMemo(() => {
    const todos = (blocksQuery.data ?? []) as Bloqueio[];
    if (origem === "tudo") return todos;
    if (origem === "google") return todos.filter((b) => b.block.externalProvider === "google");
    if (origem === "bloqueios") return todos.filter((b) => b.block.externalProvider == null);
    return [];
  }, [blocksQuery.data, origem]);

  // O que existe nos dias escondidos: reunião de sábado marcada antes, evento
  // do Google no domingo. Some da grade, mas não em silêncio.
  const escondidos = useMemo(() => {
    if (!esconderFimDeSemana) return 0;
    const fds = (d: Date | string) => {
      const x = new Date(d).getDay();
      return x === 0 || x === 6;
    };
    return (reunioes as any[]).filter((r) => fds(r.meeting.startAt)).length + bloqueios.filter((b) => fds(b.block.startAt)).length;
  }, [esconderFimDeSemana, reunioes, bloqueios]);

  function abrirBloqueio(defaults?: BlockDefaults) {
    setBloqueioDialogo({ aberto: true, bloqueio: null, defaults });
  }

  function editarBloqueio(b: Bloqueio) {
    setBloqueioDialogo({ aberto: true, bloqueio: b.block });
  }

  function abrirNova(quando?: Date) {
    setEditandoId(null);
    setDefaults(quando ? { startAt: quando } : undefined);
    setSheetAberto(true);
  }

  const rotuloPeriodo =
    visao === "mes"
      ? primeiraMaiuscula(ancora.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }))
      : visao === "semana"
      ? `${inicio.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${dias[6].toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`
      : primeiraMaiuscula(
          inicio.toLocaleDateString("pt-BR", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          }),
        );

  return (
    <div className="space-y-4">
      <Tabs value={aba} onValueChange={setAba}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
          <TabsList>
            <TabsTrigger value="calendario">
              <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
              Calendário
            </TabsTrigger>
            <TabsTrigger value="disponibilidade">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              Disponibilidade
            </TabsTrigger>
          </TabsList>

          {/* Semana típica e Google: cadastro, não rotina — moram na engrenagem,
              que só faz sentido ao lado da aba que ela configura. */}
          {aba === "disponibilidade" && (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground"
            aria-label="Configurações da agenda"
            title="Configurações da agenda"
            onClick={() => setConfigAberta(true)}
          >
            <Settings className="h-4 w-4" />
          </Button>
          )}
          </div>

          <div className="flex items-center gap-2">
          {/* Uma escolha de pessoa só, válida na grade e na disponibilidade. */}
          {podeVerTodos && (
            <Select value={pessoa} onValueChange={setPessoa}>
              <SelectTrigger className="h-8 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toda a equipe</SelectItem>
                {meuId && <SelectItem value={String(meuId)}>Só as minhas</SelectItem>}
                {(usersQuery.data ?? [])
                  .filter((u: any) => u.id !== meuId)
                  .map((u: any) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name ?? u.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          {podeMarcar && (
            <div className="flex gap-2">
              {/* Na Disponibilidade o próprio cartão de bloqueios tem o botão —
                  dois "Bloquear" na mesma tela só fazem procurar diferença. */}
              {aba === "calendario" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => abrirBloqueio({ userId: filtroPessoa })}>
                    <CalendarOff className="h-4 w-4 mr-1.5" />
                    Bloquear
                  </Button>
                  <Button size="sm" onClick={() => abrirNova()}>
                    <Plus className="h-4 w-4 mr-1.5" />
                    Nova reunião
                  </Button>
                </>
              )}
            </div>
          )}
          </div>
        </div>

        <TabsContent value="calendario" className="pt-4 space-y-4">
          {/* ── Navegação ───────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8"
              aria-label={visao === "mes" ? "Mês anterior" : visao === "semana" ? "Semana anterior" : "Dia anterior"}
              onClick={() => navegar(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setAncora(new Date())}>
              Hoje
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8"
              aria-label={visao === "mes" ? "Próximo mês" : visao === "semana" ? "Próxima semana" : "Próximo dia"}
              onClick={() => navegar(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>

            {/* O rótulo do período é o atalho para pular a uma data. */}
            <Popover open={seletorAberto} onOpenChange={setSeletorAberto}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" className="h-8 px-2 font-medium">
                  <CalendarDays className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  {rotuloPeriodo}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  locale={ptBR}
                  selected={visao === "dia" ? inicio : undefined}
                  defaultMonth={inicio}
                  onSelect={(d) => {
                    if (!d) return;
                    setAncora(d);
                    setSeletorAberto(false);
                  }}
                />
              </PopoverContent>
            </Popover>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={visao}
                onValueChange={(v) => v && setVisao(v as Visao)}
                aria-label="Modo de visualização"
              >
                <ToggleGroupItem value="dia" className="h-8 px-2.5 text-xs" aria-label="Ver um dia">
                  <RectangleVertical className="h-3.5 w-3.5 mr-1" />
                  Dia
                </ToggleGroupItem>
                <ToggleGroupItem value="semana" className="h-8 px-2.5 text-xs" aria-label="Ver a semana">
                  <Columns3 className="h-3.5 w-3.5 mr-1" />
                  Semana
                </ToggleGroupItem>
                <ToggleGroupItem value="mes" className="h-8 px-2.5 text-xs" aria-label="Ver o mês">
                  <LayoutGrid className="h-3.5 w-3.5 mr-1" />
                  Mês
                </ToggleGroupItem>
              </ToggleGroup>

              {visao !== "mes" && (
              <div className="flex items-center rounded-md border border-input">
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-r-none"
                  aria-label="Diminuir a altura das horas"
                  disabled={densidade === DENSIDADES_ORDEM[0]}
                  onClick={() => mudarDensidade(-1)}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="w-px h-5 bg-border" />
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-l-none"
                  aria-label="Aumentar a altura das horas"
                  disabled={densidade === DENSIDADES_ORDEM[DENSIDADES_ORDEM.length - 1]}
                  onClick={() => mudarDensidade(1)}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              )}

              <Select value={origem} onValueChange={(v) => setOrigem(v as Origem)}>
                <SelectTrigger className="h-8 w-40" aria-label="O que mostrar">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tudo">Tudo</SelectItem>
                  <SelectItem value="reunioes">Só reuniões</SelectItem>
                  <SelectItem value="bloqueios">Só bloqueios</SelectItem>
                  <SelectItem value="google">Só Google Agenda</SelectItem>
                </SelectContent>
              </Select>

            </div>
          </div>

          {semFimDeSemana && visao !== "dia" && (escondidos > 0 || mostrarFimDeSemana) && (
            <p className="text-xs text-muted-foreground -mt-2">
              {mostrarFimDeSemana
                ? "Fim de semana visível (sem expediente cadastrado)."
                : `${escondidos} ${escondidos === 1 ? "compromisso" : "compromissos"} no fim de semana, fora da grade.`}{" "}
              <button type="button" className="underline hover:text-foreground" onClick={() => setMostrarFimDeSemana((v) => !v)}>
                {mostrarFimDeSemana ? "Esconder fim de semana" : "Mostrar"}
              </button>
            </p>
          )}

          {/* ── Grade da semana ─────────────────────────────── */}
          {meetingsQuery.isLoading || blocksQuery.isLoading ? (
            <Skeleton className="h-[32rem] w-full" />
          ) : visao === "mes" ? (
            <MonthGrid
              dias={diasVisiveis}
              mes={ancora}
              reunioes={reunioes as any}
              bloqueios={bloqueios as any}
              modoEquipe={podeVerTodos && pessoa === "all"}
              onAbrirReuniao={(id) => setDetalheId(id)}
              onEditarReuniao={
                podeMarcar
                  ? (id) => {
                      setEditandoId(id);
                      setDefaults(undefined);
                      setSheetAberto(true);
                    }
                  : undefined
              }
              onAbrirBloqueio={podeMarcar ? editarBloqueio : undefined}
              onEscolherDia={(d) => {
                setAncora(d);
                setVisao("dia");
              }}
            />
          ) : (
            <WeekGrid
              dias={diasVisiveis}
              reunioes={reunioes as any}
              bloqueios={bloqueios as any}
              faixas={faixas as any}
              densidade={densidade}
              modoEquipe={podeVerTodos && pessoa === "all"}
              meuId={meuId}
              podeMarcar={podeMarcar}
              onAbrirReuniao={(id) => setDetalheId(id)}
              onMarcar={(quando) => abrirNova(quando)}
              onBloquear={
                podeMarcar
                  ? (ini, fim) => abrirBloqueio({ userId: filtroPessoa, startAt: ini, endAt: fim })
                  : undefined
              }
              onAbrirBloqueio={podeMarcar ? editarBloqueio : undefined}
              onEscolherDia={(d) => {
                setAncora(d);
                setVisao("dia");
              }}
            />
          )}

        </TabsContent>

        <TabsContent value="disponibilidade" className="pt-4">
          <AvailabilityPanel
            userId={filtroPessoa}
            configAberta={configAberta}
            onFecharConfig={() => setConfigAberta(false)}
          />
        </TabsContent>
      </Tabs>

      <MeetingSheet
        open={sheetAberto}
        onClose={() => setSheetAberto(false)}
        meetingId={editandoId}
        defaults={defaults}
      />

      <BlockDialog
        open={bloqueioDialogo.aberto}
        onClose={() => setBloqueioDialogo((d) => ({ ...d, aberto: false }))}
        bloqueio={bloqueioDialogo.bloqueio}
        defaults={bloqueioDialogo.defaults}
      />

      <MeetingDetailSheet
        meetingId={detalheId}
        onClose={() => setDetalheId(null)}
        onEdit={(id) => {
          setDetalheId(null);
          setEditandoId(id);
          setDefaults(undefined);
          setSheetAberto(true);
        }}
      />
    </div>
  );
}

// ─── Detalhe da reunião ──────────────────────────────────────────────────────

/** Atalho local: o componente usa o mesmo cálculo em três lugares. */
/** "Só Ana (organiza)" · "Ana + Bruno" · "Ana + 3" — igual ao formulário. */
function resumoParticipantes(attendees: any[]): string {
  const org = attendees.find((a) => a.attendee.isOrganizer) ?? attendees[0];
  const nomeOrg = primeiroNome(org?.user?.name, org?.user?.email);
  const outros = attendees.length - 1;
  if (outros <= 0) return `Só ${nomeOrg} (organiza)`;
  if (outros === 1) {
    const outro = attendees.find((a) => a !== org);
    return `${nomeOrg} + ${primeiroNome(outro?.user?.name, outro?.user?.email)}`;
  }
  return `${nomeOrg} + ${outros}`;
}

function horarioDaReuniao(m: any) {
  return horarioComFuso(m.meeting.startAt, m.meeting.endAt, m.meeting.timezone);
}

function MeetingDetailSheet({
  meetingId,
  onClose,
  onEdit,
}: {
  meetingId: number | null;
  onClose: () => void;
  onEdit: (id: number) => void;
}) {
  const { can } = usePermissions();
  const utils = trpc.useUtils();
  const [outcome, setOutcome] = useState("");
  const podeMarcar = can("agenda:manage") || can("agenda:manage_all");

  const query = trpc.agenda.meetings.get.useQuery(
    { id: meetingId ?? 0 },
    { enabled: meetingId != null },
  );

  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [reagendando, setReagendando] = useState(false);
  const [novoDia, setNovoDia] = useState("");
  const [novaHora, setNovaHora] = useState("");

  const reagendar = trpc.agenda.meetings.update.useMutation({
    onSuccess: () => {
      toast.success("Reunião remarcada");
      utils.agenda.meetings.list.invalidate();
      if (meetingId) utils.agenda.meetings.get.invalidate({ id: meetingId });
      setReagendando(false);
      onClose();
    },
    onError: (e: any) => {
      // Mesma regra do formulário: conflito com outra reunião dá para forçar;
      // bloqueio e fora do expediente (BAD_REQUEST) o servidor recusa.
      if (e?.data?.code === "CONFLICT") {
        toast.error(e.message, {
          duration: 12000,
          action: { label: "Remarcar assim mesmo", onClick: () => enviarReagendamento(true) },
        });
        return;
      }
      toast.error(toUserFriendlyError(e, "Erro ao remarcar"));
    },
  });

  function enviarReagendamento(force = false) {
    const m = query.data;
    if (!m || !novoDia || !novaHora) return;
    const inicio = inputLocalParaUtc(`${novoDia}T${novaHora}`, m.meeting.timezone);
    const minutos = duracaoEmMinutos(m.meeting.startAt, m.meeting.endAt);
    reagendar.mutate({
      id: m.meeting.id,
      startAt: inicio,
      endAt: new Date(inicio.getTime() + minutos * 60_000),
      force,
    });
  }

  const setStatus = trpc.agenda.meetings.setStatus.useMutation({
    onSuccess: (_r, vars) => {
      toast.success(vars.status === "canceled" ? "Reunião cancelada" : "Reunião atualizada");
      utils.agenda.meetings.list.invalidate();
      if (meetingId) utils.agenda.meetings.get.invalidate({ id: meetingId });
      const dealId = query.data?.meeting.dealId;
      if (dealId) utils.crm.deals.listActivities.invalidate({ dealId });
      // Registrar o desfecho encerra a conversa com a reunião: manter a caixa
      // aberta faria a pessoa procurar o que ainda falta fazer ali.
      setCancelando(false);
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar a reunião")),
  });

  const m = query.data;

  useEffect(() => {
    // Rascunho é da reunião aberta, não da próxima.
    setOutcome("");
    setMotivoCancelamento("");
    setCancelando(false);
  }, [meetingId]);

  return (
    <Dialog open={meetingId != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 flex flex-col max-h-[85vh] bg-white dark:bg-card">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <div className="flex items-start justify-between gap-3 pr-6">
            <DialogTitle>{m?.meeting.title ?? "Reunião"}</DialogTitle>
            {m && podeMarcar && m.meeting.status !== "canceled" && (
              <div className="flex gap-1.5 shrink-0">
                {/* Remarcar é o que mais acontece com uma reunião — merece um
                    caminho de dois cliques, sem abrir o formulário inteiro. */}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => {
                    const [d, h] = utcParaInputLocal(new Date(m.meeting.startAt), m.meeting.timezone).split("T");
                    setNovoDia(d);
                    setNovaHora(h);
                    setReagendando(true);
                  }}
                >
                  <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                  Reagendar
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEdit(m.meeting.id)}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Editar
                </Button>
              </div>
            )}
          </div>
          <DialogDescription>
            {m ? (
              <>
                {new Date(m.meeting.startAt).toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}{" "}
                · {horarioDaReuniao(m).principal} em {horarioDaReuniao(m).rotulo}
                {horarioDaReuniao(m).local && (
                  <>
                    {" · no seu relógio: "}
                    <strong>{horarioDaReuniao(m).local}</strong>
                  </>
                )}
              </>
            ) : (
              "Carregando…"
            )}
          </DialogDescription>
        </DialogHeader>

        {m && (
          <div className="space-y-4 px-5 pb-4 overflow-y-auto flex-1 min-h-0">
            {/* Mesma ordem e os mesmos blocos do formulário — só que lidos, não
                preenchidos: quem marcou reconhece a tela na hora. */}
            <div className="flex flex-wrap gap-1.5">
              <Badge className={`${STATUS_REUNIAO[m.meeting.status]?.tint} border-0`}>
                {STATUS_REUNIAO[m.meeting.status]?.label ?? m.meeting.status}
              </Badge>
              <Badge variant="outline" className="font-normal">{TIPO_LOCAL[m.meeting.locationType]}</Badge>
              <Badge variant="outline" className="font-normal">
                {rotuloDeDuracao(duracaoEmMinutos(m.meeting.startAt, m.meeting.endAt))}
              </Badge>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Contato</p>
                {m.contact?.id ? (
                  <Link
                    href={`/contacts/${m.contact.id}`}
                    className="block rounded-md border border-border px-2.5 py-1.5 hover:border-primary/40 transition-colors"
                  >
                    <span className="block text-sm text-primary truncate">{m.contact.name}</span>
                    {m.contact.companyName && (
                      <span className="block text-xs text-muted-foreground truncate">{m.contact.companyName}</span>
                    )}
                  </Link>
                ) : (
                  <p className="text-xs text-muted-foreground h-9 flex items-center">Sem contato.</p>
                )}
              </div>

              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Oportunidade</p>
                {m.deal?.id ? (
                  <Link
                    href={`/crm/deals/${m.deal.id}`}
                    className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-primary hover:border-primary/40 transition-colors"
                  >
                    <Briefcase className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{m.deal.title}</span>
                  </Link>
                ) : (
                  <p className="text-xs text-muted-foreground h-9 flex items-center">Sem oportunidade.</p>
                )}
              </div>

              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Quem participa
                </p>
                <div
                  className="h-9 flex items-center gap-2 rounded-md border border-border px-2.5"
                  title={m.attendees
                    .map((a: any) => `${a.user?.name ?? a.user?.email ?? "—"}${a.attendee.isOrganizer ? " (organiza)" : ""}`)
                    .join(", ")}
                >
                  <span className="flex -space-x-2 shrink-0">
                    {m.attendees.slice(0, 3).map((a: any) => (
                      <span
                        key={a.attendee.id}
                        className={`h-6 w-6 rounded-full ring-2 ring-background inline-flex items-center justify-center text-[10px] font-semibold ${tomDoAvatar(a.attendee.userId)}`}
                      >
                        {iniciais(a.user?.name, a.user?.email)}
                      </span>
                    ))}
                  </span>
                  <span className="flex-1 text-xs truncate">{resumoParticipantes(m.attendees)}</span>
                </div>
              </div>
            </div>

            {/* ── Formato ────────────────────────────────────────────── */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Formato</p>
                <p className="h-9 flex items-center rounded-md border border-border px-2.5 text-sm">
                  {TIPO_LOCAL[m.meeting.locationType]}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">
                  {m.meeting.meetingUrl ? "Link da chamada" : "Local"}
                </p>
                {m.meeting.meetingUrl ? (
                  <a
                    href={m.meeting.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 flex items-center gap-1.5 rounded-md border border-border px-2.5 text-sm text-primary hover:border-primary/40 transition-colors"
                  >
                    <Video className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Entrar na reunião</span>
                    <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                  </a>
                ) : m.meeting.locationText ? (
                  <p className="h-9 flex items-center gap-1.5 rounded-md border border-border px-2.5 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{m.meeting.locationText}</span>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground h-9 flex items-center">Não informado.</p>
                )}
              </div>
            </div>

            {m.meeting.briefing && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Briefing</p>
                <p className="text-sm whitespace-pre-wrap rounded-md bg-muted/50 p-3">
                  {m.meeting.briefing}
                </p>
              </div>
            )}

            {m.meeting.outcome && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">O que saiu da reunião</p>
                <p className="text-sm whitespace-pre-wrap rounded-md bg-muted/50 p-3">
                  {m.meeting.outcome}
                </p>
              </div>
            )}

            {podeMarcar && m.meeting.status !== "canceled" && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Registrar o resultado (opcional)
                </p>
                <Textarea
                  rows={3}
                  placeholder="O que ficou combinado, próximos passos…"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {/* Ações fora da área rolável: o que fazer com a reunião fica sempre à
            vista, mesmo num briefing longo. */}
        {m && podeMarcar && m.meeting.status !== "canceled" && (
          <div className="border-t bg-white dark:bg-card px-5 py-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* O desfecho é uma escolha entre dois estados, e o atual fica
                      marcado: sem isso, quem reabre a reunião não sabe se já
                      registrou. O que estiver escrito acima vai junto nos dois. */}
                  <Button
                    size="sm"
                    variant={m.meeting.status === "done" ? "default" : "outline"}
                    disabled={setStatus.isPending}
                    onClick={() =>
                      setStatus.mutate({
                        id: m.meeting.id,
                        status: "done",
                        outcome: outcome.trim() || null,
                      })
                    }
                  >
                    {m.meeting.status === "done" && <Check className="h-3.5 w-3.5 mr-1.5" />}
                    Realizada
                  </Button>
                  <Button
                    size="sm"
                    variant={m.meeting.status === "no_show" ? "default" : "outline"}
                    disabled={setStatus.isPending}
                    onClick={() =>
                      setStatus.mutate({
                        id: m.meeting.id,
                        status: "no_show",
                        outcome: outcome.trim() || null,
                      })
                    }
                  >
                    {m.meeting.status === "no_show" && <Check className="h-3.5 w-3.5 mr-1.5" />}
                    Não compareceu
                  </Button>
                  <button
                    type="button"
                    className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors underline-offset-2 hover:underline"
                    disabled={setStatus.isPending}
                    onClick={() => setCancelando(true)}
                  >
                    Cancelar reunião
                  </button>
                </div>
          </div>
        )}
      </DialogContent>

      {/* Reagendar: só data e hora. A duração e o resto da reunião ficam como
          estão — quem quer mexer neles usa Editar. */}
      <Dialog open={reagendando} onOpenChange={(o) => !o && setReagendando(false)}>
        <DialogContent className="sm:max-w-sm bg-white dark:bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              Reagendar
            </DialogTitle>
            <DialogDescription>
              {m
                ? `Hoje: ${new Date(m.meeting.startAt).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })} · ${horarioDaReuniao(m).principal} (${rotuloDeDuracao(duracaoEmMinutos(m.meeting.startAt, m.meeting.endAt))})`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Novo dia</Label>
              <Input type="date" className="mt-1 w-[10.5rem]" value={novoDia} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNovoDia(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Início</Label>
              <Input type="time" className="mt-1 w-[6.5rem]" value={novaHora} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNovaHora(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            A duração continua a mesma. Os participantes veem a mudança na própria agenda.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReagendando(false)}>
              Voltar
            </Button>
            <Button
              size="sm"
              disabled={reagendar.isPending || !novoDia || !novaHora}
              onClick={() => enviarReagendamento(false)}
            >
              {reagendar.isPending ? "Remarcando…" : "Remarcar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar é o único caminho sem volta aqui: pede confirmação e o motivo,
          que fica no histórico e é o que os participantes vão ler. */}
      <AlertDialog open={cancelando} onOpenChange={(o) => !o && setCancelando(false)}>
        <AlertDialogContent className="bg-white dark:bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar esta reunião?</AlertDialogTitle>
            <AlertDialogDescription>
              Ela sai da agenda de todos os participantes e o horário fica livre de novo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="text-xs">Motivo do cancelamento</Label>
            <Textarea
              className="mt-1"
              rows={3}
              placeholder="O cliente pediu para remarcar, conflito de agenda…"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <Button
              variant="destructive"
              size="sm"
              disabled={setStatus.isPending || !motivoCancelamento.trim()}
              onClick={() =>
                m &&
                setStatus.mutate({
                  id: m.meeting.id,
                  status: "canceled",
                  canceledReason: motivoCancelamento.trim(),
                })
              }
            >
              {setStatus.isPending ? "Cancelando…" : "Cancelar reunião"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export default function Agenda() {
  return (
    <PermissionGuard permission="agenda:view">
      <DashboardLayout>
        <AgendaContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
